import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";

export default function Login() {
  const navigate = useNavigate();
  const { user, loading, login } = useAuth();
  const { companyName, logoUrl } = useBranding();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate("/", { replace: true });
  }, [loading, user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setSubmitting(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Inloggad");
      navigate("/", { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Inloggning misslyckades";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="login-page"
      className="min-h-screen flex items-center justify-center bg-cover bg-center relative"
      style={{
        backgroundImage:
          'url("https://images.unsplash.com/photo-1591638212063-c2f69701a931?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwxfHx3b29kd29ya2luZyUyMHN0YWlycyUyMG1vZGVybiUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3ODAxNDQzMDh8MA&ixlib=rb-4.1.0&q=85")',
      }}
    >
      <div className="absolute inset-0 bg-black/55" />
      <div className="relative z-10 bg-surface p-10 md:p-12 rounded-2xl max-w-md w-full mx-4 border border-app">
        {logoUrl && (
          <div className="mb-4" data-testid="login-logo">
            <img
              src={logoUrl}
              alt={companyName}
              className="max-h-16 max-w-[180px] object-contain"
              data-testid="login-logo-image"
            />
          </div>
        )}
        <div className="mb-2 text-xs tracking-[0.2em] uppercase text-text-muted" data-testid="login-company-name">
          {companyName}
        </div>
        <h1 className="font-heading text-4xl md:text-5xl tracking-tight text-text-primary leading-[1.05]">
          Tidsrapporten
          <br />
          <span className="text-brand">i arbetet.</span>
        </h1>
        <p className="mt-4 text-text-muted text-sm">
          Logga in med din e-post och ditt lösenord för att stämpla in på objekt och moment.
        </p>

        <form onSubmit={handleSubmit} className="mt-8 space-y-4">
          <div>
            <label className="block text-xs text-text-muted mb-1.5" htmlFor="email">
              E-post
            </label>
            <input
              id="email"
              type="email"
              required
              data-testid="login-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              className="tk-input w-full"
              placeholder="namn@foretag.se"
            />
          </div>
          <div>
            <label className="block text-xs text-text-muted mb-1.5" htmlFor="password">
              Lösenord
            </label>
            <input
              id="password"
              type="password"
              required
              data-testid="login-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              className="tk-input w-full"
              placeholder="••••••••"
            />
            <div className="mt-2 text-right">
              <a
                href="/forgot-password"
                data-testid="login-forgot-password-link"
                className="text-xs text-brand hover:underline"
              >
                Glömt lösenord?
              </a>
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid="login-submit-button"
            className="tk-btn-primary w-full mt-2"
          >
            {submitting ? "Loggar in…" : "Logga in"}
          </button>
        </form>

        <p className="mt-6 text-[11px] text-text-muted">
          {companyName} · Saknar du konto? Kontakta din admin.
          <br />
          Ny här? Prova <span className="text-text-primary">Tidsrapporten</span> gratis i 30 dagar — inget kort krävs.{" "}
          <a href="/register" className="text-brand hover:underline" data-testid="login-register-link">
            Starta gratis prövotid
          </a>{" "}
          ·{" "}
          <a href="/pricing" className="text-brand hover:underline" data-testid="login-pricing-link">
            Se priser
          </a>
        </p>
      </div>
    </div>
  );
}
